import simpleRestProvider from "ra-data-simple-rest";
import type {
  DataProvider,
  GetListParams,
  GetOneParams,
  GetManyParams,
  GetManyReferenceParams,
  CreateParams,
  UpdateParams,
  UpdateManyParams,
  DeleteParams,
  DeleteManyParams,
} from "react-admin";
import { fetchUtils } from "react-admin";
import { TOKEN_KEY, refreshAccessToken } from "./authProvider";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

// All admin resources live under /admin
const ADMIN_URL = `${API_URL}/admin`;

export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Custom httpClient that adds Bearer auth and handles 401 → refresh
async function httpClient(
  url: string,
  options: fetchUtils.Options = {}
): Promise<{
  status: number;
  headers: Headers;
  body: string;
  json: unknown;
}> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = new Headers(options.headers ?? {});
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetchUtils.fetchJson(url, { ...options, headers });

  return response;
}

// Wrapped httpClient with 401-refresh logic.
// Reuses the shared refreshAccessToken() helper to deduplicate concurrent calls.
async function authHttpClient(
  url: string,
  options: fetchUtils.Options = {}
): Promise<{
  status: number;
  headers: Headers;
  body: string;
  json: unknown;
}> {
  try {
    return await httpClient(url, options);
  } catch (error) {
    // fetchUtils.fetchJson throws on non-2xx; check for 401
    const fetchError = error as { status?: number };
    if (fetchError.status === 401) {
      // Delegate to the shared refresh helper (deduplicates concurrent callers,
      // also persists aere_user from the refresh response).
      await refreshAccessToken();
      // Retry with the new token.
      return httpClient(url, options);
    }
    throw error;
  }
}

const baseDataProvider = simpleRestProvider(ADMIN_URL, authHttpClient);

const dataProvider: DataProvider = {
  getList: (resource: string, params: GetListParams) =>
    baseDataProvider.getList(resource, params),

  getOne: (resource: string, params: GetOneParams) =>
    baseDataProvider.getOne(resource, params),

  getMany: (resource: string, params: GetManyParams) =>
    baseDataProvider.getMany(resource, params),

  getManyReference: (resource: string, params: GetManyReferenceParams) =>
    baseDataProvider.getManyReference(resource, params),

  create: <RecordType extends { id: string | number }>(
    resource: string,
    params: CreateParams
  ) => baseDataProvider.create<RecordType>(resource, params),

  update: <RecordType extends { id: string | number }>(
    resource: string,
    params: UpdateParams
  ) => baseDataProvider.update<RecordType>(resource, params),

  updateMany: (resource: string, params: UpdateManyParams) =>
    baseDataProvider.updateMany(resource, params),

  delete: <RecordType extends { id: string | number }>(
    resource: string,
    params: DeleteParams
  ) => baseDataProvider.delete<RecordType>(resource, params),

  deleteMany: (resource: string, params: DeleteManyParams) =>
    baseDataProvider.deleteMany(resource, params),
};

export { API_URL, ADMIN_URL };
export default dataProvider;
